import { Record, String as RTString, Static } from 'runtypes';
import { buildService } from './decorated-service';
import { getCustomerData } from './customer-service';
import { getCustomerHouseholdData } from './household-service';
import { isTrust, parseCustomerData } from '../utils';

import settings from '../../../settings.json';
import { callApigeeCookie } from './cookie-service';
import { initiateAuthService } from '../loaders';
import { StateStore } from '../../../core/types/application-state';
import config from '../../../config';

const UserRegistrationService = buildService('/nasu-svc/service/user');
const UserRegistrationSearchService = buildService(
  '/nasu-svc/service/user/search'
);
const UserRegistrationValidationService = buildService(
  '/nasu-svc/service/validation/nasu-customer'
);
const UserRegistrationServiceWithTempPassword = buildService(
  '/nasu-svc/service/user-temp-password'
);
const DeleteDelegateCookieService = buildService(
  '/nasu-svc/service/utility/clear-delegate-user'
);

const CreateCredentialsArgs = Record({
  loginID: RTString,
  password: RTString,
  firstName: RTString,
  lastName: RTString,
  emailAddress: RTString
});

const CreateTempCredentialsArgs = Record({
  loginID: RTString,
  firstName: RTString,
  lastName: RTString,
  emailAddress: RTString,
  bpid: RTString,
  userAgent: RTString,
  rsaDeviceToken: RTString
});

export type StaticCreateCredentialsArgs = Static<typeof CreateCredentialsArgs>;
export type StaticCreateTempCredentialsArgs = Static<
  typeof CreateTempCredentialsArgs
>;

export const createUser = (
  args: Partial<StaticCreateCredentialsArgs>
): Promise<any> => {
  const validated: StaticCreateCredentialsArgs = CreateCredentialsArgs.check(
    args
  );
  return UserRegistrationService.add(validated);
};

export const createUserWithTempPassword = (
  args: Partial<StaticCreateTempCredentialsArgs>
): Promise<any> => {
  const validated: StaticCreateTempCredentialsArgs = CreateTempCredentialsArgs.check(
    args
  );
  return UserRegistrationServiceWithTempPassword.add(validated);
};

const GetEnablementArgs = Record({
  taxID: RTString,
  firstName: RTString,
  lastName: RTString,
  dateOfBirth: RTString
});

export type StaticGetEnablementArgs = Static<typeof GetEnablementArgs>;

// @TODO [2020-05-25] Talk to backend about
// why fetching data is using wrong HTTP semantic (POST instead of GET)
// this is not RESTful

const mockUserSearchData = {
  enabled: false,
  retailEnabled: false,
  rpsEnabled: false,
  registeredOnlyWithAgreements: false,
  redirectToLogin: false
};

const mockUserValidationData = {
  validationStatus: 'VALID'
};

export const getEnablementStatusService = async (
  args: Partial<StaticGetEnablementArgs>
): Promise<any> => {
  if (settings['mockedMode']) return mockUserSearchData;
  /* account for prospect user types that will have no ssn, and therefore not enabled */
  if (!args?.taxID) return { enabled: false };

  const validated: StaticGetEnablementArgs = GetEnablementArgs.check(args);
  return UserRegistrationSearchService.add(validated);
};

export const getUserValidationStatusService = async (
  args: Partial<StaticGetEnablementArgs>
): Promise<any> => {
  if (settings['mockedMode']) return mockUserValidationData;
  const validated: StaticGetEnablementArgs = GetEnablementArgs.check(args);
  return UserRegistrationValidationService.add(validated);
};

const RegisterAndLoginUserArgs = Record({
  firstName: RTString,
  lastName: RTString,
  emailAddress: RTString,
  loginID: RTString,
  password: RTString
});
type StaticRegisterAndLoginUserArgs = Static<typeof RegisterAndLoginUserArgs>;

export const registerAndLoginUserService = async (
  args: Partial<StaticRegisterAndLoginUserArgs>
): Promise<any> => {
  const { firstName, lastName, emailAddress, loginID, password } = args;
  const { loginToken } = await createUser({
    loginID,
    password,
    firstName,
    lastName,
    emailAddress
  });

  await callApigeeCookie(loginToken);
  const authDetails = await initiateAuthService();
  return authDetails;
};

export const registerUserWithTempPassword = async (
  args: Partial<StaticCreateTempCredentialsArgs>
): Promise<any> => {
  const {
    firstName,
    lastName,
    emailAddress,
    loginID,
    bpid,
    userAgent,
    rsaDeviceToken
  } = args;
  const results = await createUserWithTempPassword({
    loginID,
    firstName,
    lastName,
    emailAddress,
    bpid,
    userAgent,
    rsaDeviceToken
  });
  return results;
};

/* eslint-disable complexity */
export const authenticateUser = async (
  appStateStore: StateStore,
  accountType
): Promise<any> => {
  const emptySummitData = {
    totalAssetBalanceAmount: 0,
    assetTier: 'NONE',
    assetTierID: 0
  };
  const { authenticated } = await initiateAuthService();
  const keyedCustomerDataFromStore = isTrust(accountType)
    ? { trustee: appStateStore?.app?.trustee }
    : { customer: appStateStore?.app?.customer };

  if (authenticated) {
    const customerData = await getCustomerData();

    const householdData = await getCustomerHouseholdData();

    const summitData = {
      // using minimumAmount property for now until service returns actual totalAssetBalanceAmount
      totalAssetBalanceAmount:
        householdData?.householdBalanceTierStatus?.householdAssetTier
          ?.minimumAmount,
      assetTier:
        householdData?.householdBalanceTierStatus?.householdAssetTier?.name,
      assetTierID:
        householdData?.householdBalanceTierStatus?.householdAssetTier?.id
    };

    // account for non-successful customer data query with authentication only set if non-401 response
    // DE70920 - in event of 204 response, return customer data within store (accounts for RJ data already preloaded)
    if (typeof customerData === 'number')
      return {
        authenticated: customerData !== 401,
        enabled: false,
        summitData,
        ...(customerData === 204 && { ...keyedCustomerDataFromStore })
      };

    const {
      firstName,
      lastName,
      dateOfBirthString: dob,
      taxID,
      prospect
    } = customerData;

    // once we have customer data, check users enablement status
    const enabledRes = await getEnablementStatusService({
      taxID: taxID || '',
      firstName,
      lastName,
      dateOfBirth: dob || ''
    });

    // returns in the form of `customer` or `trustee` key'd object
    const parsedCustomerDataForAppState = parseCustomerData(
      appStateStore,
      customerData,
      accountType
    );

    return {
      authenticated,
      enabled: enabledRes?.enabled,
      retailEnabled: enabledRes?.retailEnabled,
      prospect,
      summitData,
      ...parsedCustomerDataForAppState
    };
  }

  return {
    authenticated,
    enabled: false,
    prospect: false,
    ...keyedCustomerDataFromStore,
    summitData: emptySummitData
  };
};

export const delegateCookieDelete = async (): Promise<void> => {
  await DeleteDelegateCookieService.index({
    headers: {
      'accept-version': 2.0,
      'content-type': 'application/json',
      apikey: config('apiKey'),
      'x-trp-requested-with': 'XMLHttpRequest'
    }
  });
};
